import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle, 
  Star, 
  Building, 
  Truck, 
  Award,
  Users,
  Clock,
  Shield
} from 'lucide-react';

const Home = () => {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();

  const productCategories = [
    /*
    {
      key: 'building',
      image: 'https://images.pexels.com/photos/237950/pexels-photo-237950.jpeg',
      icon: Building
    },
    */
    {
      key: 'electrical',
      image: 'https://images.pexels.com/photos/32588549/pexels-photo-32588549.jpeg',
      icon: Building
    },
    {
      key: 'fixtures',
      image: 'https://images.unsplash.com/photo-1714399913207-b9198ba711f9',
      icon: Building
    },
    /*
    {
      key: 'lighting',
      image: 'https://images.unsplash.com/photo-1603545440473-5f2a1f8f014e',
      icon: Building
    },
    */
    {
      key: 'stones',
      image: 'https://images.unsplash.com/photo-1584354273341-3eb96574e5be',
      icon: Building
    },
    /*
    {
      key: 'steel',
      image: 'https://images.pexels.com/photos/46167/iron-rods-reinforcing-bars-rods-steel-bars-46167.jpeg',
      icon: Building
    },
    */
    {
      key: 'sanitary',
      image: 'https://images.pexels.com/photos/6032203/pexels-photo-6032203.jpeg',
      icon: Building
    },
    {
      key: 'tiles',
      image: 'https://images.unsplash.com/photo-1580398562556-d33329a0f29b',
      icon: Building
    }
  ];

  const features = [
    {
      icon: Award,
      title: language === 'en' ? 'Premium Quality' : 'جودة عالية',
      description: language === 'en' 
        ? 'Certified materials meeting international standards'
        : 'مواد معتمدة تلبي المعايير الدولية'
    },
    {
      icon: Truck,
      title: language === 'en' ? 'Fast Delivery' : 'توصيل سريع',
      description: language === 'en' 
        ? 'Reliable delivery across the UAE'
        : 'توصيل موثوق في جميع أنحاء الإمارات'
    },
    {
      icon: Users,
      title: language === 'en' ? 'Expert Support' : 'دعم الخبراء',
      description: language === 'en' 
        ? '24/7 technical assistance and consultation'
        : 'مساعدة تقنية واستشارة على مدار الساعة'
    },
    {
      icon: Shield,
      title: language === 'en' ? 'Warranty Guarantee' : 'ضمان الجودة',
      description: language === 'en' 
        ? 'Comprehensive warranty on all products'
        : 'ضمان شامل على جميع المنتجات'
    }
  ];

  const stats = [
    { number: '500+', label: language === 'en' ? 'Projects Completed' : 'مشروع مكتمل' },
    { number: '1000+', label: language === 'en' ? 'Happy Clients' : 'عميل سعيد' },
    { number: '15+', label: language === 'en' ? 'Years Experience' : 'سنة خبرة' },
    { number: '24/7', label: language === 'en' ? 'Support Available' : 'دعم متاح' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with Parallax Effect */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{ 
              backgroundImage: `url('https://images.pexels.com/photos/2115367/pexels-photo-2115367.jpeg')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-purple-900/90" />
          <div className="absolute inset-0 bg-islamic-pattern" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <motion.h1 
              className={`text-5xl md:text-7xl font-bold mb-6 ${isRTL ? 'font-arabic' : ''} leading-tight`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t('hero.title')}
            </motion.h1>
            
            <motion.p 
              className={`text-xl md:text-2xl mb-4 text-yellow-300 font-semibold ${isRTL ? 'font-arabic' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t('hero.subtitle')}
            </motion.p>
            
            <motion.p 
              className={`text-lg md:text-xl mb-10 text-gray-200 max-w-3xl mx-auto leading-relaxed ${isRTL ? 'font-arabic' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {t('hero.description')}
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link
                to="/quote"
                className="group bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25 hover:scale-105 flex items-center space-x-2 rtl:space-x-reverse"
              >
                <span>{t('hero.cta')}</span>
                <ArrowRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
              <Link
                to="/products"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-300 shadow-xl hover:scale-105"
              >
                {t('hero.cta2')}
              </Link>
            </motion.div>
          </motion.div>

          {/* Floating Elements */}
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-16 h-16 bg-yellow-400/20 rounded-full blur-sm"
          />
          <motion.div
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 right-10 w-24 h-24 bg-blue-400/20 rounded-full blur-sm"
          />
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`text-center ${isRTL ? 'font-arabic' : ''}`}
              >
                <div className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm md:text-base text-gray-200">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center mb-16 ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('products.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('products.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {productCategories.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group"
              >
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={category.image}
                      alt={t(`products.categories.${category.key}`)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className={`text-white font-bold text-lg ${isRTL ? 'font-arabic text-right' : ''}`}>
                        {t(`products.categories.${category.key}`)}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <Link
                      to={`/products#${category.key}`}
                      className="inline-flex items-center space-x-2 rtl:space-x-reverse text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                    >
                      <span>{t('products.viewAll')}</span>
                      <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/products"
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center space-x-2 rtl:space-x-reverse"
            >
              <span>{t('products.viewAll')}</span>
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center mb-16 ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Why Choose Noor Altayseer?' : 'لماذا تختار نور التيسير؟'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'Your trusted partner for premium construction materials and exceptional service in the UAE'
                : 'شريكك الموثوق لمواد البناء المتميزة والخدمة الاستثنائية في دولة الإمارات'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={`text-center group ${isRTL ? 'font-arabic' : ''}`}
              >
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`max-w-4xl mx-auto ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'en' 
                ? 'Ready to Start Your Construction Project?'
                : 'هل أنت مستعد لبدء مشروعك الإنشائي؟'
              }
            </h2>
            <p className="text-xl mb-10 text-gray-200">
              {language === 'en' 
                ? 'Get a competitive quote for your building materials today. Our experts are ready to help you succeed.'
                : 'احصل على عرض سعر تنافسي لمواد البناء الخاصة بك اليوم. خبراؤنا مستعدون لمساعدتك على النجاح.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/quote"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-xl hover:shadow-yellow-500/25 hover:scale-105 inline-flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <span>{t('nav.quote')}</span>
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-300 shadow-xl hover:scale-105"
              >
                {t('nav.contact')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;